import { Link } from '@mui/material';

import { useForm } from 'react-hook-form';

import { Link as RouterLink, useNavigate } from 'react-router';

import { ClassValidatorResolver } from '@app-velocity/validators';
import { useI18n } from '@app-velocity/i18n';
import {
  Form,
  TextField,
  PageHeader,
  ErrorMessage,
  Button,
} from '@app-velocity/ui';

import { AuthRoutesPaths } from '@app-velocity/libs/auth';

import { UserResetPasswordDto } from '../../dto/UserResetPasswordDto';

import { useResetPasswordMutation } from '../../queries/useResetPasswordMutation';

export const ForgotPasswordPage = () => {
  const navigate = useNavigate();

  const { t } = useI18n();

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors },
  } = useForm<UserResetPasswordDto>({
    resolver: ClassValidatorResolver(UserResetPasswordDto),
  });

  const {
    mutate: resetPassword,
    data,
    error,
    isPending,
  } = useResetPasswordMutation();

  const onSubmit = (data: UserResetPasswordDto) => {
    resetPassword(data);
  };

  const handleSignInClick = () => {
    navigate(AuthRoutesPaths.Login);
  };

  if (data) {
    return (
      <>
        <PageHeader>
          <PageHeader.Title>
            {t('users:forgot_password.check_your_email')}
          </PageHeader.Title>
          <PageHeader.Subtitle>
            {t('users:forgot_password.instructions_sent')}
          </PageHeader.Subtitle>
        </PageHeader>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSignInClick}
        >
          {t('users:auth.sign_in')}
        </Button>
      </>
    );
  }

  return (
    <>
      <PageHeader>
        <PageHeader.Title>{t('users:forgot_password.title')}</PageHeader.Title>
      </PageHeader>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        data-testid="form-forgot-password"
      >
        <Form.FieldContainer>
          <Form.Label>{t('users:forgot_password.email')}</Form.Label>
          <TextField
            name="email"
            control={control}
            placeholder={t('users:forgot_password.email_placeholder')}
            fullWidth
            helperText={formErrors.email?.message}
            error={!!formErrors.email}
            inputProps={{
              autoComplete: 'email',
              'data-testid': 'form-forgot-password-email',
            }}
          />
        </Form.FieldContainer>

        <ErrorMessage error={error} />

        <Form.ButtonsContainer>
          <Button
            fullWidth
            disabled={isPending}
            type="submit"
            variant="contained"
            data-testid="form-forgot-password-submit"
            size="large"
          >
            {t('users:forgot_password.send_recovery_email')}
          </Button>
          <Form.SecondaryText>
            {t('users:forgot_password.remembered_the_password')}{' '}
            <Link component={RouterLink} to={AuthRoutesPaths.Login}>
              {t('users:auth.sign_in')}
            </Link>
          </Form.SecondaryText>
        </Form.ButtonsContainer>
      </Form>
    </>
  );
};
